import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { registerSW } from 'virtual:pwa-register';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initializeCacheManagement } from './utils/cacheManager';

// Detect Lovable preview / iframe / dev contexts where the service worker
// causes stale builds (e.g. Geomaps hero not loading the latest assets).
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const host = window.location.hostname;
const isPreviewHost =
  host.includes('id-preview--') ||
  host.includes('lovableproject.com') ||
  host.includes('lovable.app') && host.includes('--') ||
  host === 'localhost' ||
  host === '127.0.0.1';

const disableSW = import.meta.env.DEV || isInIframe || isPreviewHost;

if (disableSW) {
  // Aggressively tear down any previously installed service worker + caches
  // so the latest build is always served in preview environments.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister().catch(() => {}));
    }).catch(() => {});
  }
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((n) => caches.delete(n).catch(() => {}));
    }).catch(() => {});
  }
} else {
  // Production-only service worker registration with update strategy
  const updateSW = registerSW({
    onNeedRefresh() {
      updateSW(true);
    },
    onOfflineReady() {},
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => {
          if (!registration.active || registration.installing) return;
          registration.update().catch(() => {
            // InvalidStateError or network errors are non-fatal
          });
        }, 600000); // 10 min
      }
    },
  });
}

// Initialize cache management (deferred to not block render)
requestIdleCallback(() => initializeCacheManagement(), { timeout: 3000 });

// Initialize platform detection so <html data-platform="…"> is set before
// the first paint. Hooks read from this same module-level state.
import { getPlatformSnapshot } from "@/hooks/usePlatform";
getPlatformSnapshot();

// Auth hygiene: if a previous OAuth (Google/Apple) Supabase session is sitting
// in localStorage but no custom session exists, flush it once. This prevents
// the Supabase client from auto-refreshing a stale session and emitting
// SIGNED_IN events that re-bridge the old identity (the bug where starting an
// email OTP login silently signs you in as a previous Google account, and the
// random "signed in / signed out" flicker on focus in preview).
import { flushStaleSupabaseSessionOnBoot } from "@/utils/authBoot";
flushStaleSupabaseSessionOnBoot();

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

